'use client';

import { removeEmptyObject, sortObject } from '@utils/commonUtils';
import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useRouter } from 'next/navigation';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/src/contexts/ModalsContext';
import qs from 'qs';
import { useCallback, useContext } from 'react';
import { checkAppVersion } from '@wconcept/wconcept-common/utils/userAgentUtil';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { CustomNavigateOptions } from '@contexts/NavigationContext';
import { isIOS, isSafari } from 'react-device-detect';
import MainSlideModal2 from '@wconcept/wconcept-ui-renewal/components/organisms/_modal/MainSlideModal2';
import useGlobalState from './useGlobalState';

// Version comparison utility function
const compareVersions = (v1: string, v2: string): number => {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  return 0;
};

// 현재 버전보다 낮거나 같은 버전 중 가장 높은 버전을 찾습니다
// 그 버전의 nativePageList에서 해당 경로가 있는지 확인합니다
const checkPathInNativePageList = (
  path: string,
  currentVersion: string,
  nativePageList: Record<string, string[]>
): boolean => {
  // nativePageList의 버전을 내림차순으로 정렬합니다
  const versions = Object.keys(nativePageList).sort((a, b) =>
    compareVersions(b, a)
  );

  // 현재 버전보다 낮거나 같은 버전 중 가장 높은 버전을 찾습니다
  let targetVersion = versions[versions.length - 1]; // 기본값으로 최소 버전 설정
  for (const version of versions) {
    if (compareVersions(currentVersion, version) >= 0) {
      targetVersion = version;
      break;
    }
  }
  // 찾은 버전의 nativePageList에서 해당 경로가 있는지 확인
  return nativePageList[targetVersion].some(page => path.includes(page));
};

function formatUrl(url: string, domain = window.location.origin) {
  // URL이 http:// 또는 https://로 시작하는지 확인
  const hasProtocol = /^https?:\/\//i.test(url);

  // 도메인이 이미 포함되어 있으면 원래 URL 반환
  if (hasProtocol) {
    return url;
  }

  // 도메인 끝의 슬래시 제거
  const cleanDomain = domain.endsWith('/') ? domain.slice(0, -1) : domain;
  // 경로 시작의 슬래시 보장
  const cleanPath = url.startsWith('/') ? url : `/${url}`;

  return `${cleanDomain}${cleanPath}`;
}

const moveToPage = async (url: string): Promise<boolean> => {
  try {
    // iOS
    if (window.webkit?.messageHandlers?.wckAppInterface) {
      return await new Promise(resolve => {
        window.navigateToCallback = (result: boolean) => {
          resolve(result);
        };

        window.webkit.messageHandlers.wckAppInterface.postMessage(
          JSON.stringify({
            url,
            callback: 'navigateToCallback',
          })
        );
      });
    }

    // Android
    if (window.wckAppInterface?.navigateTo) {
      return await window.wckAppInterface.navigateTo(
        JSON.stringify({
          url,
        })
      );
    }

    return await Promise.resolve(false);
  } catch (error) {
    console.error('Router bridge error:', error);
    return Promise.resolve(false);
  }
};

// 7.0.0 이전 버전 네이티브 앱 버전 페이지 리스트 7 버전 부터는 앱에서 체크해줌
const nativePageList = {
  '6.1.4': ['/new', '/lnb', '/category', '/women', '/search', '/best'],
};

// next 13전용
const useHistoryRouter = () => {
  const router = useRouter();
  const { closeModal } = useContext(ModalsContext);
  const { isMobile, appInfo, deviceInfo } = useGlobalState();
  const queryClient = useQueryClient();
  const linkToPush = useCallback(
    async ({
      url,
      option,
    }: {
      url: string;
      option?: CustomNavigateOptions;
    }) => {
      const queryData = queryClient.getQueriesData({
        queryKey: ['wckQueryKey'],
      });
      const filteredQueryData: Record<string, unknown> = {};
      queryData.forEach((query: [QueryKey, unknown]) => {
        const [queryKey, queryValue] = query;
        filteredQueryData[queryKey.join()] = queryValue;
      });
      window.history.replaceState(
        {
          ...window.history.state,
          filteredQueryData,
        },
        '',
        undefined
      );

      // 피씨에서 열려있는 바텀 팝업 닫기
      if (!isMobile) closeModal(MainSlideModal2);
      let targetUrl = url;
      const tempUrl = removeEmptyObject(
        sortObject(qs.parse(url.split('?')[1], { ignoreQueryPrefix: true }))
      );
      if (tempUrl?.keys?.length > 0) {
        targetUrl = `${url.split('?')[0]}?${qs.stringify(tempUrl)}`;
      }
      if (option?.scroll === false) {
        router.push(targetUrl, option);
      }
      const forceReloadOption: CustomNavigateOptions = {
        forceReload: true,
      };
      if (appInfo.isNativeAppBoth) {
        if (option?.parallel) {
          router.push(targetUrl, option);
        } else {
          router.push(targetUrl, forceReloadOption);
        }
        // const checkVersion = checkAppVersion(deviceInfo?.version, '7.0.0');
        // const fullUrl = formatUrl(url);
        // if (checkVersion === 'low') {
        //   // 네이티브 앱 버전이 낮은데 nativePageList에 포함되어 있으면 location.href를 사용.
        //   const nativeUrl = new URL(fullUrl);

        //   if (
        //     nativeUrl.origin !== window.location.origin ||
        //     checkPathInNativePageList(
        //       nativeUrl.pathname,
        //       deviceInfo?.version || '',
        //       nativePageList
        //     )
        //   ) {
        //     router.push(targetUrl, forceReloadOption as NavigateOptions);
        //   } else {
        //     // 경로가 NativePageList에 없으면 router.push를 사용.
        //     // eslint-disable-next-line no-lonely-if
        //     // 아래내용 주석처리 하는 이유는 사파리 이슈 때문에 추가했다가 일단 주석처리
        //     // if (isSafari) {
        //     //   if (option?.parallel) {
        //     //     router.push(targetUrl, option as NavigateOptions);
        //     //   } else {
        //     //     router.push(targetUrl, forceReloadOption as NavigateOptions);
        //     //   }
        //     // } else {
        //     //   router.push(targetUrl, option as NavigateOptions);
        //     // }
        //     router.push(targetUrl, option as NavigateOptions);
        //   }
        // } else {
        //   const isNativePage = await moveToPage(fullUrl);
        //   if (!isNativePage) {
        //     router.push(targetUrl, option as NavigateOptions);
        //   }
        // }
      } else if (typeof url === 'string') {
        if (option?.parallel) {
          router.push(targetUrl, option);
        } else {
          router.push(targetUrl, forceReloadOption);
        }
        // let urlObject;
        // try {
        //   urlObject = new URL(targetUrl);
        // } catch (e) {
        //   console.log('url parse error', e);
        // }
        // if (targetUrl.startsWith('?')) {
        //   // 쿼리스트링만 있는 경우 예) ?a=1&b=2
        //   router.push(targetUrl, option);
        // } else if (
        //   urlObject &&
        //   urlObject.origin === process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB
        // ) {
        //   // 현재 도메인에서 열려있는 페이지인 경우 예) https://display.wconcept.co.kr/pathname?a=1&b=2
        //   router.push(`${urlObject.pathname}?${qs.stringify(tempUrl)}`, option);
        // } else if (urlObject && urlObject.origin !== window.location.origin) {
        //   // 다른 도메인에서 열려있는 페이지인 경우 예) https://www.naver.com/pathname?a=1&b=2
        //   router.push(targetUrl, forceReloadOption);
        // } else {
        //   // 현재 도메인에서 열려있는 페이지인 경우 ex) /pathname
        //   router.push(targetUrl, option);
        // }

        // if (isSafari && option?.parallel) {
        //   router.push(targetUrl, option);
        // } else if (isSafari) {
        //   router.push(targetUrl, forceReloadOption);
        // } else {
        //   router.push(targetUrl, option);
        // }
      }
    },
    [queryClient, isMobile, closeModal, appInfo, router]
  );

  const linkToReplace = useCallback(
    async ({
      url,
      option,
    }: {
      url: string;
      option?: CustomNavigateOptions;
    }) => {
      let targetUrl = url;
      const tempUrl = removeEmptyObject(
        sortObject(qs.parse(url.split('?')[1], { ignoreQueryPrefix: true }))
      );
      if (tempUrl) {
        targetUrl = `${url.split('?')[0]}?${qs.stringify(tempUrl)}`;
      }

      if (appInfo.isNativeAppBoth) {
        const isNativePage = await moveToPage(formatUrl(url));
        if (!isNativePage) {
          router.replace(targetUrl, option);
        }
      } else if (typeof url === 'string') {
        router.replace(targetUrl, option);
      }
    },
    [appInfo.isNativeAppBoth, router]
  );

  // isRouterBack: true -> router.back
  // or 앱스킴 마지막 else router back
  const linkToBack = useCallback(
    (option?: CustomNavigateOptions) => {
      if (option?.parallel) {
        router.back();
      } else if (appInfo?.isNativeAppBoth) {
        window.location.href = 'wconceptkr://webViewBack';
      } else {
        router.back();
      }
    },
    [router, appInfo?.isNativeAppBoth]
  );

  return {
    linkTo: {
      push: linkToPush,
      replace: linkToReplace,
      back: linkToBack,
    },
  };
};

export default useHistoryRouter;
